<template>
  <div class="container-fluid users-management">
    <div v-if="isLoading" class= "loader-section">
      <frontoffice-loader />
    </div>

    <div v-else>
      <div class="fixed-top sticky-header">
        <div class="col-12 filters-title">
          <page-title>
            <span>{{ $t('home.users.users_management') }}</span>

            <user-edit @input="updateUsers" />
          </page-title>
        </div>

        <b-container>
          <b-form class="form-inline row justify-content-center"
                  @submit.prevent="updateUsers"
          >
            <div class="col-10 col-md-6 col-lg-5 col-xl-5 offset-xl-1 display">
              <text-input id="inlineFormInputName" name="filter-text"
                          label="user.per_text" :required="false" v-model="filter"
              />
            </div>

            <b-col cols="12" md="5" lg="4" xl="3"
                   class="text-center text-md-left filters-buttons"
            >
              <input type="button" class="btn orange-white" :value="$t('_validate')"
                     name="filter" v-on:click="updateUsers"
              />

              <input type="button" class="btn orange-white" :value="$t('_reset')"
                     name="reset" v-on:click="resetFilter"
              />
            </b-col>
          </b-form>
        </b-container>
      </div>

      <div>
        <b-table striped hover
                 :items="users"
                 :fields="fields"
                 :busy="isUpdating"
                 :per-page="perPage"
                 id="users"
                 :show-empty="true"
                 :empty-text="$t('_no_result_found')"
                 @sort-changed="onSort"
        >
          <template :mq="viewPorts.mobile" slot="show_details" slot-scope="row">
            <button @click="row.toggleDetails" class="frame toggle">
              <i v-if="row.detailsShowing" class="fas fa-minus"></i>
              <i  v-else class="fas fa-plus"></i>
            </button>
          </template>

          <template :mq="viewPorts.mobile" slot="row-details" slot-scope="data">
            <b-row>
              <b-col cols="12" class="detailCell-wrapper">
                <span class="title">{{ $t('_mobile_phone') }} : </span>
                <span class="value">{{ data.item.mobile_phone }}</span>
              </b-col>

              <b-col cols="12" class="detailCell-wrapper">
                <span class="title">{{ $t('_landline') }} : </span>
                <span class="value">{{ data.item.landline }}</span>
              </b-col>

              <b-col cols="12" class="detailCell-wrapper">
                <span class="title">{{ $t('_email') }} : </span>
                <span class="value">
                  <a :href="'mailto:' + data.item.email">{{ data.item.email }}</a>
                </span>
              </b-col>

              <b-col cols="12" class="detailCell-wrapper">
                <span class="title">{{ $t('_role') }} : </span>
                <span class="value">{{ formatUserRole(data.item.is_manger) }}</span>
              </b-col>

              <b-col cols="12" class="detailCell-wrapper">
                <span class="title">{{ $t('_enabled') }} : </span>
                <span class="value">{{ $t(formatBool(data.item.enabled, false)) }}</span>
              </b-col>

              <b-col class="btn-wrapper">
                <span class="title">{{ $t('_actions') }} : </span>

                <user-edit :user="data.item" @input="updateUsers()" />

                <list-btn :default-state="data.item.enabled ? 'enable' : 'disable'"
                          :callback="onSwapDisable"
                          :id="data.item.id"
                          :class="data.item.enabled ? 'btn-enable':'btn-disable'"
                />

              </b-col>
            </b-row>
          </template>

          <template :mq="viewPorts.desktop" slot="email" slot-scope="data">
            <a :href="'mailto:' + data.value">{{ data.value }}</a>
          </template>

          <template :mq="viewPorts.desktop" slot="enabled" slot-scope="data">
            {{ $t(formatBool(data.value, false)) }}
          </template>

          <template :mq="viewPorts.desktop" slot="is_manager" slot-scope="data">
            {{ formatUserRole(data.value) }}
          </template>

          <template :mq="viewPorts.desktop" slot="btns" slot-scope="data">
            <div class="btn-wrapper">
              <user-edit :user="data.item" @input="updateUsers()" />

              <list-btn :default-state="data.item.enabled ? 'enable' :'disable'"
                        :callback="onSwapDisable"
                        :id="data.item.id"
                        :class="data.item.enabled ? 'btn-enable':'btn-disable'"
              />
            </div>
          </template>

          <template slot="table-busy">{{ $t('_loading') }}</template>
        </b-table>

        <div v-if="nbUsers">
          <b-pagination v-model="currentPage" size="md" :total-rows="nbUsers"
                        :per-page="perPage" aria-controls="users"
                        @input="updateUsers"
          />

          <p class="mt-3">
            {{ nbUsers }} {{ $tc('_user_s', nbUsers) }}
          </p>
        </div>
      </div>
    </div>
  </div>
  <!-- </div> -->
</template>

<script>
import viewPort from "@/conf/viewPort";
import frontofficeLoader from '@/components/frontoffice/loader'
import pageTitle from '@/components/frontoffice/pageTitle'
import CommonApi from '@/api/commonApi'
import formatter from '@/components/global/mixins/formatterMethods'
import textInput from '@/components/frontoffice/form/baseInput/text'
import listBtn from '@/components/frontoffice/button/list'
import userEdit from '@/components/frontoffice/form/user/edit'

export default {
  components: {
    frontofficeLoader,
    pageTitle,
    textInput,
    listBtn,
    userEdit,
  },

  mixins: [formatter, viewPort],

  data () {
    return {
      isLoading: true,
      isUpdating: true,
      commonApi: new CommonApi(),
      perPage: 10,
      currentPage: 1,
      users: [],
      nbUsers: null,
      fieldsDefinition: {
        mobile: [
          'show_details',
          { key: 'gender',        sortable: true, label: this.$t('_gender_short')},
          { key: 'first_name',    sortable: true, label: this.$t('_first_name')},
          { key: 'last_name',     sortable: true, label: this.$t('_last_name')},
        ],
        desktop: [
          { key: 'gender',        sortable: false, label: this.$t('_gender_short')},
          { key: 'first_name',    sortable: true, label: this.$t('_first_name')},
          { key: 'last_name',     sortable: true, label: this.$t('_last_name')},
          { key: 'landline',      sortable: true, label: this.$t('_landline')},
          { key: 'mobile_phone',  sortable: false, label: this.$t('_mobile_phone')},
          { key: 'email',         sortable: true, label: this.$t('_email')},
          { key: 'is_manager',    sortable: false, label: this.$t('user.role')},
          { key: 'enabled',       sortable: true, label: this.$t('_enabled')},
          { key: 'btns',          sortable: false, label: this.$t('_actions')}
        ]
      },
      filter: '',
      order: '',
      orderBy: ''
    }
  },

  created () {
    this.updateUsers(true)
  },

  methods: {
    updateUsers (init = false) {
      if (init) this.isLoading = true
      else this.isUpdating = true

      this.commonApi.listUsers(this.perPage, this.currentPage, this.orderBy, this.order, this.filter)
      .then(response => {
        this.nbUsers = response.data.data.total
        let users = []

        for (let user of response.data.data.selection) {
          users.push({
            id: user.id,
            gender: this.formatGender(user.gender),
            first_name: this.formatToUCFirst(user.first_name),
            last_name: user.last_name.toUpperCase(),
            landline: this.getFormattedPhoneNumber(user.landline),
            mobile_phone: this.getFormattedPhoneNumber(user.mobile_phone),
            email: user.email,
            email2: user.email2,
            is_manager: user.is_manager,
            enabled: user.enabled,
            exclude_non_free:
              user.exclude_online_non_free_cancellation === false
                ? false
                : user.exclude_online_non_free_cancellation || null,
            max_price: user.max_price_per_room,
            override_max_price: user.max_price_per_room !== undefined,
            override_exclude_non_free: user.exclude_online_non_free_cancellation !== undefined,
            override_unknown_breakfast: user.exclude_online_unknown_breakfast !== undefined,
            send_voucher_to_travellers: user.send_voucher_to_travellers || false
          })
        }

        this.users = users
      }).then(response => {
        this.isLoading = false
        this.isUpdating = false
      })
    },

    onSort (ctx) {
      this.orderBy = ctx.sortBy
      this.order = ctx.sortDesc ? 'DESC' : 'ASC'

      this.updateUsers()
    },

    onSwapDisable (userId) {
      this.commonApi.swapUserEnabled(userId).then(response => {
        if (response.data.success) this.updateUsers()
      })
    },

    resetFilter () {
      this.filter = ''
      this.updateUsers()
    },

    getFormattedPhoneNumber (phone) {
      if (phone !== undefined && phone.trim().length) return this.formatPhoneNumber(phone)
      else return ''
    }
  },

  computed: {
    fields () {
      let key = this.isViewPortMobile ? 'mobile' : this.isViewPortDesktop ? 'desktop' : null
      return this.fieldsDefinition[key]
    }
  }
}
</script>
